import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { createOrder } from '../api/ordersApi';
import PublicCartSidebar from '../components/PublicCartSidebar';
import PublicDeliveryModal from '../components/PublicDeliveryModal';
import PublicFooter from '../components/PublicFooter';
import PublicNavbar from '../components/PublicNavbar';
import { CartItem, isMissingDeliveryAddress, PublicDeliveryInfo } from '../types/store';

type PublicCheckoutPageProps = {
  userName: string;
  deliveryInfo: PublicDeliveryInfo;
  cartItems: CartItem[];
  cartUnits: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onRemoveCartItem: (itemId: string) => void;
  onDeliveryChange: (delivery: PublicDeliveryInfo) => void;
  onConfirmOrder: () => void;
  onLogout: () => void;
};

export default function PublicCheckoutPage({
  userName,
  deliveryInfo,
  cartItems,
  cartUnits,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onRemoveCartItem,
  onDeliveryChange,
  onConfirmOrder,
  onLogout,
}: PublicCheckoutPageProps) {
  const navigate = useNavigate();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (cartItems.length === 0 && !isSuccessModalOpen) {
    return <Navigate to="/productos" replace />;
  }

  const cartTotal = cartItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const missingAddress = isMissingDeliveryAddress(deliveryInfo.address);

  const handleConfirmOrder = async () => {
    if (missingAddress || isSubmitting) {
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const result = await createOrder({
        customerName: userName,
        customerPhone: deliveryInfo.phone,
        deliveryAddress: deliveryInfo.address,
        deliveryReference: deliveryInfo.reference,
        items: cartItems.map((item) => ({
          productId: item.id,
          name: item.name,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
        total: cartTotal,
      });

      setOrderNumber(result.orderNumber);
      onConfirmOrder();
      setIsSuccessModalOpen(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'No se pudo confirmar el pedido');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    navigate('/inicio');
  };

  return (
    <main className="public-page-shell">
      <PublicNavbar
        deliveryInfo={deliveryInfo}
        cartUnits={cartUnits}
        onOpenCart={onOpenCart}
        onDeliveryChange={onDeliveryChange}
      />

      {!isSuccessModalOpen ? (
        <section className="card content-card public-checkout-card">
          <h2>Finalizar pedido</h2>
          <p className="subtitle">Revisa los productos de tu carrito antes de confirmar.</p>

          <div className="checkout-items">
            {cartItems.map((item) => (
              <article key={item.id} className="checkout-item-card">
                <div className="cart-item-thumb">
                  {item.image ? (
                    <img src={item.image} alt={item.name} />
                  ) : (
                    item.icon || '💊'
                  )}
                </div>
                <div className="checkout-item-content">
                  <p className="cart-item-name">{item.name}</p>
                  <p className="subtitle">Cantidad: {item.quantity}</p>
                  <p className="checkout-item-price">S/. {item.unitPrice.toFixed(2)} c/u</p>
                </div>
                <strong className="checkout-item-subtotal">
                  S/. {(item.quantity * item.unitPrice).toFixed(2)}
                </strong>
              </article>
            ))}
          </div>

          <div className="checkout-total-row">
            <span>Total</span>
            <strong>S/. {cartTotal.toFixed(2)}</strong>
          </div>

          <div className="checkout-delivery-section">
            <p className="checkout-delivery-label">Los productos seran enviados a</p>
            <div className="checkout-delivery-card">
              <div className="checkout-delivery-info">
                {missingAddress ? (
                  <p className="checkout-delivery-missing">Aun no has registrado una direccion de entrega.</p>
                ) : (
                  <>
                    <p className="checkout-delivery-address">{deliveryInfo.address}</p>
                    <p className="subtitle">Telefono: {deliveryInfo.phone}</p>
                    {deliveryInfo.reference ? (
                      <p className="subtitle">Referencia: {deliveryInfo.reference}</p>
                    ) : null}
                  </>
                )}
              </div>
              <button
                type="button"
                className="checkout-delivery-edit"
                onClick={() => setIsAddressModalOpen(true)}
              >
                Editar
              </button>
            </div>
          </div>

          {submitError ? <p className="error checkout-submit-error">{submitError}</p> : null}

          <button
            type="button"
            className="checkout-confirm-button"
            disabled={missingAddress || isSubmitting}
            onClick={handleConfirmOrder}
          >
            {isSubmitting ? 'Confirmando...' : 'Confirmar pedido'}
          </button>
        </section>
      ) : null}

      <PublicFooter onLogout={onLogout} />

      <PublicDeliveryModal
        isOpen={isAddressModalOpen}
        deliveryInfo={deliveryInfo}
        onClose={() => setIsAddressModalOpen(false)}
        onSubmit={onDeliveryChange}
      />

      {isSuccessModalOpen ? (
        <div className="modal-overlay modal-overlay-locked" role="presentation">
          <div
            className="modal-content modal-content-sm checkout-success-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-success-title"
          >
            <div className="checkout-success-icon" aria-hidden="true">
              ✓
            </div>
            <h3 id="checkout-success-title">Tu pedido ha sido confirmado</h3>
            <p className="checkout-success-message success">
              ¡Gracias por tu compra! Hemos recibido tu pedido correctamente.
            </p>
            {orderNumber ? (
              <p className="checkout-order-number">
                Tu numero de pedido es <strong>{orderNumber}</strong>
              </p>
            ) : null}
            <p className="checkout-success-note">
              Nuestro driver se estara comunicando contigo cuando este llegando a tu domicilio.
            </p>
            <button type="button" className="checkout-success-button" onClick={handleCloseSuccessModal}>
              Entendido
            </button>
          </div>
        </div>
      ) : null}

      <PublicCartSidebar
        isOpen={isCartOpen}
        cartItems={cartItems}
        onClose={onCloseCart}
        onRemoveItem={onRemoveCartItem}
      />
    </main>
  );
}
